import { z } from 'zod'

/**
 * What a proposal bids: the trees, the irrigation, or both. Chosen by staff
 * per proposal (see "On the proposal" in irrigation.md).
 *
 * Like a version's status, this has NO Prisma counterpart: it is stored as two
 * flags, `includesTrees` and `includesIrrigation`, because the document
 * freezes it that way. A proposal bidding nothing is not a kind of proposal,
 * so the choice is three values rather than two flags that could both be off.
 */
export const PROPOSAL_WORKS = ['TREES', 'IRRIGATION', 'BOTH'] as const

export const proposalWorkSchema = z.enum(PROPOSAL_WORKS)

export type ProposalWork = z.infer<typeof proposalWorkSchema>

export const PROPOSAL_WORK_LABELS: Record<ProposalWork, string> = {
  TREES: 'Trees',
  IRRIGATION: 'Irrigation',
  BOTH: 'Both',
}

interface WorkFlags {
  includesTrees: boolean
  includesIrrigation: boolean
}

/**
 * The choice the stored flags make. Neither cannot be chosen, and reads as
 * trees: what every proposal bid before irrigation could be.
 */
export function proposalWorkOf(flags: WorkFlags): ProposalWork {
  if (flags.includesIrrigation) {
    return flags.includesTrees ? 'BOTH' : 'IRRIGATION'
  }
  return 'TREES'
}

/** The flags a choice is stored as. */
export function proposalWorkFlags(work: ProposalWork): WorkFlags {
  return {
    includesTrees: work !== 'IRRIGATION',
    includesIrrigation: work !== 'TREES',
  }
}
